import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  bulkProductsApi,
  type BulkPricePayload,
  type BulkCategoryPayload,
  type BulkThresholdPayload,
  type BulkResult,
} from "@/lib/admin-api/bulk-products";

function isMakerCheckerResult(
  result: BulkResult,
): result is { pending_action_id: string } {
  return "pending_action_id" in result;
}

function handleBulkResult(result: BulkResult, actionLabel: string): void {
  if (isMakerCheckerResult(result)) {
    toast.info("En attente d'approbation par la Direction");
    return;
  }
  toast.success(`${(result as { affected?: number }).affected ?? 0} produit(s) ${actionLabel}`);
}

export function useBulkActions() {
  const queryClient = useQueryClient();
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["admin", "products"] });

  const activate = useMutation({
    mutationFn: (ids: readonly string[]) => bulkProductsApi.activate(ids),
    onSuccess: (r) => {
      handleBulkResult(r, "active(s)");
      invalidate();
    },
    onError: () => toast.error("Erreur lors de l'activation"),
  });

  const deactivate = useMutation({
    mutationFn: (ids: readonly string[]) => bulkProductsApi.deactivate(ids),
    onSuccess: (r) => {
      handleBulkResult(r, "desactive(s)");
      invalidate();
    },
    onError: () => toast.error("Erreur lors de la desactivation"),
  });

  const bulkDelete = useMutation({
    mutationFn: (ids: readonly string[]) => bulkProductsApi.delete(ids),
    onSuccess: (r) => {
      handleBulkResult(r, "supprime(s)");
      invalidate();
    },
    onError: () => toast.error("Erreur lors de la suppression"),
  });

  const restore = useMutation({
    mutationFn: (ids: readonly string[]) => bulkProductsApi.restore(ids),
    onSuccess: (r) => {
      handleBulkResult(r, "restaure(s)");
      invalidate();
    },
    onError: () => toast.error("Erreur lors de la restauration"),
  });

  const price = useMutation({
    mutationFn: (payload: BulkPricePayload) => bulkProductsApi.price(payload),
    onSuccess: (r) => {
      handleBulkResult(r, "-- prix mis a jour");
      invalidate();
    },
    onError: () => toast.error("Erreur lors de la mise a jour des prix"),
  });

  const category = useMutation({
    mutationFn: (payload: BulkCategoryPayload) =>
      bulkProductsApi.category(payload),
    onSuccess: (r) => {
      handleBulkResult(r, "-- categorie mise a jour");
      invalidate();
    },
    onError: () => toast.error("Erreur lors du changement de categorie"),
  });

  const threshold = useMutation({
    mutationFn: (payload: BulkThresholdPayload) =>
      bulkProductsApi.threshold(payload),
    onSuccess: (r) => {
      handleBulkResult(r, "-- seuil mis a jour");
      invalidate();
    },
    onError: () => toast.error("Erreur lors de la mise a jour du seuil"),
  });

  const isAnyLoading =
    activate.isPending ||
    deactivate.isPending ||
    bulkDelete.isPending ||
    restore.isPending ||
    price.isPending ||
    category.isPending ||
    threshold.isPending;

  return {
    activate,
    deactivate,
    bulkDelete,
    restore,
    price,
    category,
    threshold,
    isAnyLoading,
  } as const;
}
